import type { CSSProperties } from "react";

import { cn } from "@/lib/utils";

/**
 * Heavyweight section header used to break a long page into clearly
 * delimited sections. Renders as a short filled bar — sized like the
 * feedback Alerts — with the tone colour anchored at the bottom edge
 * (where the old 1px rule sat) blooming upward into the bar, and the
 * label set inside it. Used on the CRO forecast Summary tab to split
 * Month-to-Date from Quarter-to-Date and on the KPI dashboards to head
 * each section.
 *
 * Props:
 * - ``label`` — required headline (e.g. "March 2026"); rendered uppercase
 *   in the cinch design system's eyebrow style.
 * - ``description`` — optional second line under the label.
 * - ``tone`` — visual emphasis. ``accent`` is the brand-cyan glow for
 *   primary section headers; ``muted`` (default) is a neutral grey glow
 *   for lower-key breaks.
 * - ``align`` — left | center (default) | right.
 */
export type SectionDividerTone = "muted" | "accent";
export type SectionDividerAlign = "left" | "center" | "right";

/**
 * The bar fill is a vertical gradient laid over the card surface: the tone
 * colour sits dense at the bottom edge and fades to transparent toward the
 * top. Driven off the brand / muted CSS vars so it tracks light & dark mode.
 */
const TONE_FILL: Record<SectionDividerTone, CSSProperties> = {
  accent: {
    backgroundColor: "hsl(var(--card))",
    backgroundImage:
      "linear-gradient(to top, hsl(var(--cinch-brand) / 0.34), hsl(var(--cinch-brand) / 0.07) 68%, transparent)",
  },
  muted: {
    backgroundColor: "hsl(var(--card))",
    backgroundImage:
      "linear-gradient(to top, hsl(var(--muted-foreground) / 0.18), hsl(var(--muted-foreground) / 0.03) 70%, transparent)",
  },
};

const TONE_BORDER: Record<SectionDividerTone, string> = {
  muted: "border-border",
  accent: "border-brand/30",
};

const TONE_LABEL: Record<SectionDividerTone, string> = {
  muted: "text-muted-foreground",
  accent: "text-brand-deep",
};

const ALIGN_CLASS: Record<SectionDividerAlign, string> = {
  left: "items-start text-left",
  center: "items-center text-center",
  right: "items-end text-right",
};

export function SectionDivider({
  label,
  description,
  tone = "muted",
  align = "center",
  className,
}: {
  label: string;
  description?: string;
  tone?: SectionDividerTone;
  align?: SectionDividerAlign;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "my-3 flex min-h-[3.25rem] flex-col justify-center gap-0.5 rounded-lg border px-5 py-3",
        ALIGN_CLASS[align],
        TONE_BORDER[tone],
        className,
      )}
      style={TONE_FILL[tone]}
      role="separator"
      aria-label={label}
    >
      <span
        className={cn(
          "text-xs font-semibold uppercase tracking-[0.14em]",
          TONE_LABEL[tone],
        )}
      >
        {label}
      </span>
      {description && (
        <span className="text-xs text-muted-foreground">{description}</span>
      )}
    </div>
  );
}
